import { findSurfaceLayer, type SurfaceLayerId } from '@/terrain/TerrainSurface';
import type {
  PipelineArtifacts,
  PipelineStageId,
  SemanticCategory,
  SemanticMap,
} from '@/reference';

/**
 * Turning a stage artifact into shapes drawn over the reference photograph.
 *
 * This is the part of the inspector that makes the pipeline legible rather than merely visible. A
 * JSON dump answers "what did the model say"; the overlay answers "is it in the right place", which
 * is the question a wrong site actually raises — and it answers it in the one frame where the
 * developer already knows what the truth looks like.
 *
 * Everything here is pure and works in **normalized image space** (`[0, 1]`, origin top-left), so
 * the same shapes render at any display size and the module can be tested without a DOM. Stage 1
 * already speaks that space; stages 2–5 speak world metres and are projected back through the site
 * extent stage 1 established:
 *
 *     u = x / widthMeters + 0.5
 *     v = z / lengthMeters + 0.5
 *
 * That projection is deliberately the naive one, and its errors are informative. On an oblique
 * photograph a correctly-read driveway will land in the wrong part of the frame, which is exactly
 * the signal that the stage did — or did not — apply the perspective correction its prompt asked
 * for. Hiding that behind a fitted homography would hide the bug.
 */

export type OverlayShapeKind = 'polygon' | 'polyline' | 'point';

export interface OverlayShape {
  kind: OverlayShapeKind;
  /** Vertices in normalized image space. A `point` carries exactly one. */
  points: [number, number][];
  label: string;
  /** `#rrggbb`. Fill for polygons, stroke for lines and points. */
  color: string;
  /** `0..1`, shown as opacity so a low-confidence region reads as tentative. */
  confidence: number;
  /** Extra detail for the hover title. */
  detail?: string;
}

/** Colours per semantic category. Chosen to stay distinguishable over a photograph. */
const CATEGORY_COLORS: Record<SemanticCategory, string> = {
  building: '#e8603c',
  paving: '#8d8f98',
  water: '#2f8fd0',
  lawn: '#6fae3f',
  'planting-bed': '#a5713f',
  tree: '#1f7a4d',
  shrub: '#43a06b',
  'bare-earth': '#b08b5e',
  vehicle: '#c8b03a',
  unmodelled: '#9a6bd0',
};

export function categoryColor(category: SemanticCategory): string {
  return CATEGORY_COLORS[category] ?? '#9a6bd0';
}

export function surfaceColor(material: SurfaceLayerId): string {
  return findSurfaceLayer(material)?.color ?? '#888888';
}

/**
 * Project a world point onto the image, using the site extent stage 1 fixed.
 *
 * Returns coordinates outside `[0, 1]` when the point is outside the site rather than clamping —
 * an overlay shape that spills past the frame edge is a finding, not a rendering artifact.
 */
export function worldToImage(
  x: number,
  z: number,
  site: { widthMeters: number; lengthMeters: number },
): [number, number] {
  return [x / Math.max(1e-6, site.widthMeters) + 0.5, z / Math.max(1e-6, site.lengthMeters) + 0.5];
}

/**
 * Shapes for one stage, or an empty list when that stage has produced nothing yet.
 *
 * `fallbackSite` is the extent to project world coordinates through when stage 1 has not run. A map
 * frame already knows the site's extent exactly — it is measured rather than estimated — so a
 * capture whose structures came from a survey can be drawn over the photograph immediately, with no
 * model call anywhere in the chain. Without it the overlay stayed blank until stage 1 had been paid
 * for, which hid the one result that costs nothing.
 */
export function overlayShapes(
  stage: PipelineStageId,
  artifacts: Partial<PipelineArtifacts>,
  fallbackSite?: { widthMeters: number; lengthMeters: number },
): OverlayShape[] {
  const segment = artifacts.segment;
  const site = segment?.site ?? fallbackSite;
  if (!site) return [];

  switch (stage) {
    case 'segment':
      return segment ? segmentShapes(segment) : [];

    case 'terrain':
      return (artifacts.terrain?.controlPoints ?? []).map((point) => ({
        kind: 'point' as const,
        points: [worldToImage(point.x, point.z, site)],
        label: `${point.elevationMeters >= 0 ? '+' : ''}${round(point.elevationMeters, 2)} m`,
        // Raised ground reads warm, cut ground cool, so the grade is readable at a glance.
        color: point.elevationMeters >= 0 ? '#d9762f' : '#3f7fd0',
        confidence: 1,
        detail: `radius ${round(point.radiusMeters, 1)} m${point.notes ? ` · ${point.notes}` : ''}`,
      }));

    case 'surfaces':
      return (artifacts.surfaces?.regions ?? []).map((region) => ({
        kind: 'polygon' as const,
        points: region.polygon.map(([x, z]) => worldToImage(x, z, site)),
        label: region.material,
        color: surfaceColor(region.material),
        confidence: 1,
        detail: `feather ${round(region.featherMeters, 2)} m${region.regionId ? ` · from ${region.regionId}` : ''}`,
      }));

    case 'flora':
      return floraShapes(artifacts, site);

    case 'structures':
      return structureShapes(artifacts, site);
  }
}

function segmentShapes(segment: SemanticMap): OverlayShape[] {
  return segment.regions.map((region) => ({
    kind: 'polygon' as const,
    points: region.polygon,
    label: region.label,
    color: categoryColor(region.category),
    confidence: region.confidence,
    detail: `${region.category} · ${region.id}${region.notes ? ` · ${region.notes}` : ''}`,
  }));
}

/**
 * Planting masses drawn in whichever form they were expressed.
 *
 * A drift becomes a circle approximated as a polygon, a line becomes a polyline, a region becomes
 * its rectangle — because *which form the stage chose* is itself the thing worth seeing. A hedge
 * that came back as a scattered region rather than a line is a specific, fixable mistake, and it is
 * invisible in a plant count.
 */
function floraShapes(
  artifacts: Partial<PipelineArtifacts>,
  site: { widthMeters: number; lengthMeters: number },
): OverlayShape[] {
  const shapes: OverlayShape[] = [];

  for (const planting of artifacts.flora?.plantings ?? []) {
    const color = speciesColor(planting.speciesId);

    if (planting.line) {
      shapes.push({
        kind: 'polyline',
        points: planting.line.points.map(([x, z]) => worldToImage(x, z, site)),
        label: planting.name,
        color,
        confidence: 1,
        detail: `${planting.speciesId} · line at ${round(planting.line.spacingMeters, 2)} m`,
      });
      continue;
    }

    for (const drift of planting.drifts ?? []) {
      shapes.push({
        kind: 'polygon',
        points: circle(drift.x, drift.z, drift.radiusMeters, site),
        label: planting.name,
        color,
        confidence: 1,
        detail: `${planting.speciesId} · drift of ${drift.count}`,
      });
    }

    for (const region of planting.regions ?? []) {
      shapes.push({
        kind: 'polygon',
        points: [
          worldToImage(region.x0, region.z0, site),
          worldToImage(region.x1, region.z0, site),
          worldToImage(region.x1, region.z1, site),
          worldToImage(region.x0, region.z1, site),
        ],
        label: planting.name,
        color,
        confidence: 1,
        detail: `${planting.speciesId} · ${planting.count ?? 0} scattered`,
      });
    }
  }

  return shapes;
}

/** Every mass of every structure, so an L-shaped house shows as the two volumes it is. */
function structureShapes(
  artifacts: Partial<PipelineArtifacts>,
  site: { widthMeters: number; lengthMeters: number },
): OverlayShape[] {
  const shapes: OverlayShape[] = [];

  for (const structure of artifacts.structures?.structures ?? []) {
    const color = structure.kind === 'building' ? '#e8603c' : surfaceish(structure.paving);

    if (structure.masses && structure.masses.length > 0) {
      for (const mass of structure.masses) {
        shapes.push({
          kind: 'polygon',
          points: mass.polygon.map(([x, z]) => worldToImage(x, z, site)),
          label: mass.name ? `${structure.name} · ${mass.name}` : structure.name,
          color,
          confidence: 1,
          detail: `${round(mass.heightMeters, 1)} m to eaves · ${mass.roof} roof`,
        });
      }
      continue;
    }

    if (structure.polygon) {
      shapes.push({
        kind: 'polygon',
        points: structure.polygon.map(([x, z]) => worldToImage(x, z, site)),
        label: structure.name,
        color,
        confidence: 1,
        detail: structure.kind === 'paving' ? (structure.paving ?? 'paving') : 'building',
      });
      continue;
    }

    // The rectangle fallback, drawn as the rectangle it is so it is obvious which form was used.
    if (structure.widthMeters !== undefined && structure.depthMeters !== undefined) {
      const cx = structure.x ?? 0;
      const cz = structure.z ?? 0;
      const hw = structure.widthMeters / 2;
      const hd = structure.depthMeters / 2;
      const angle = ((structure.rotationDegrees ?? 0) * Math.PI) / 180;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const corners: [number, number][] = [
        [-hw, -hd],
        [hw, -hd],
        [hw, hd],
        [-hw, hd],
      ];
      shapes.push({
        kind: 'polygon',
        points: corners.map(([lx, lz]) =>
          worldToImage(cx + lx * cos + lz * sin, cz - lx * sin + lz * cos, site),
        ),
        label: structure.name,
        color,
        confidence: 1,
        detail: 'rectangle fallback — no outline was given',
      });
    }
  }

  return shapes;
}

function surfaceish(paving: string | undefined): string {
  if (paving === 'water') return '#2f8fd0';
  if (paving === 'asphalt') return '#3a393c';
  if (paving === 'deck') return '#8a6338';
  return '#8d8f98';
}

/** A stable colour per species id, so the same mass keeps its colour between runs. */
export function speciesColor(speciesId: string): string {
  let hash = 0;
  for (let i = 0; i < speciesId.length; i += 1) {
    hash = (Math.imul(hash, 31) + speciesId.charCodeAt(i)) | 0;
  }
  // Greens and blue-greens only: these are plants, and a magenta hedge reads as an error.
  const hue = 90 + (Math.abs(hash) % 90);
  return `hsl(${hue} 55% 45%)`;
}

function circle(
  x: number,
  z: number,
  radius: number,
  site: { widthMeters: number; lengthMeters: number },
  segments = 20,
): [number, number][] {
  const points: [number, number][] = [];
  for (let i = 0; i < segments; i += 1) {
    const angle = (i / segments) * Math.PI * 2;
    points.push(worldToImage(x + Math.cos(angle) * radius, z + Math.sin(angle) * radius, site));
  }
  return points;
}

function round(value: number, places: number): number {
  return Math.round(value * 10 ** places) / 10 ** places;
}

/** `points` as an SVG `points` attribute, scaled to a viewBox of `size` x `size`. */
export function svgPoints(points: readonly [number, number][], size = 1000): string {
  return points.map(([u, v]) => `${round(u * size, 2)},${round(v * size, 2)}`).join(' ');
}
